/**
 * API del portal del paciente.
 *
 * Todo lo de aquí devuelve SOLO lo del paciente autenticado (y lo de los
 * menores a su cargo). No hay ninguna función que reciba un id de paciente:
 * el backend lo saca del token, y el cliente no tiene forma de pedir otra cosa.
 */
import { ApiError, apiFetch } from "./client";

export interface PersonaPortal {
  paciente_id: number;
  nombre_completo: string;
  tipo_documento: string;
  num_documento: string | null;
  fecha_nacimiento: string | null;
  es_menor: boolean;
  email: string | null;
  telefono: string | null;
}

export interface MePortal {
  yo: PersonaPortal;
  a_mi_cargo: PersonaPortal[];
  debe_cambiar_password: boolean;
  policlinico: string;
  novedades: {
    resultados_listos?: number;
    recetas_pendientes?: number;
    proximas_citas?: number;
  };
}

export interface CitaPortal {
  id: number;
  paciente_id: number;
  paciente_nombre: string;
  inicio: string;
  especialidad: string | null;
  profesional: string | null;
  sede: string | null;
  /** Ya traducido a algo que el paciente entiende. */
  estado: string;
  pagada: boolean;
  monto: string | null;
}

export interface RecetaItemPortal {
  descripcion: string;
  dosis: string | null;
  frecuencia: string | null;
  duracion: string | null;
  cantidad: number;
  en_farmacia: boolean;
}

export interface RecetaPortal {
  id: number;
  paciente_id: number;
  paciente_nombre: string;
  fecha: string;
  profesional: string | null;
  indicaciones: string | null;
  estado: string;
  /** Con la firma de puño y letra escaneada. */
  tiene_firma: boolean;
  items: RecetaItemPortal[];
  total_en_farmacia: number;
  total_fuera: number;
}

export interface ResultadoItemPortal {
  descripcion: string;
  valor: string | null;
  unidad: string | null;
  valor_referencia: string | null;
  fuera_de_rango: boolean;
  texto: string | null;
  tiene_resultado: boolean;
}

export interface OrdenPortal {
  id: number;
  paciente_id: number;
  paciente_nombre: string;
  fecha: string;
  profesional: string | null;
  estado: string;
  estado_legible: string;
  items: ResultadoItemPortal[];
  listo: boolean;
}

export interface TokensPortal {
  access_token: string;
  refresh_token: string;
  access_expires_at: string;
  refresh_expires_at: string;
}

/** Entra con documento o correo. El backend decide por si hay "@". */
export function loginPortal(
  slug: string,
  identificador: string,
  password: string,
): Promise<TokensPortal> {
  return apiFetch<TokensPortal>("/v1/portal/auth/login", {
    method: "POST",
    auth: false,
    body: { slug, identificador, password },
  });
}

export function getMePortal(): Promise<MePortal> {
  return apiFetch<MePortal>("/v1/portal/me");
}

export function cambiarPasswordPortal(
  password_actual: string,
  password_nueva: string,
): Promise<void> {
  return apiFetch<void>("/v1/portal/auth/password", {
    method: "POST",
    body: { password_actual, password_nueva },
  });
}

/** Solo contacto: la identidad se corrige en admisión. */
export function actualizarMisDatos(datos: {
  email?: string | null;
  telefono?: string | null;
}): Promise<PersonaPortal> {
  return apiFetch<PersonaPortal>("/v1/portal/me/datos", {
    method: "PATCH",
    body: datos,
  });
}

export function misCitas(proximas = true): Promise<CitaPortal[]> {
  return apiFetch<CitaPortal[]>(`/v1/portal/citas?proximas=${proximas}`);
}

export function misRecetas(): Promise<RecetaPortal[]> {
  return apiFetch<RecetaPortal[]>("/v1/portal/recetas");
}

export function misResultados(): Promise<OrdenPortal[]> {
  return apiFetch<OrdenPortal[]>("/v1/portal/resultados");
}


/**
 * Abre un PDF del portal en otra pestaña.
 *
 * Se baja con fetch y se abre como blob, en vez de poner la URL en un <a>: una
 * etiqueta no manda la cabecera de autorización, y la alternativa —meter el
 * token en la dirección— lo dejaría escrito en el historial del navegador y en
 * los registros del servidor. Un token de salud en un log es un token filtrado.
 */
async function abrirPdf(path: string, nombre: string): Promise<void> {
  const { getPortalTokens } = await import("../auth/tokens");
  const token = getPortalTokens()?.access_token;
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, null, "No se pudo abrir el PDF");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Se libera al rato: revocarla en el acto cancela la descarga en algunos
  // navegadores antes de que llegue a empezar.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function descargarReceta(recetaId: number): Promise<void> {
  return abrirPdf(`/api/v1/portal/recetas/${recetaId}/pdf`, `receta-${recetaId}.pdf`);
}

/** La receta firmada a mano, que es la que exigen algunas farmacias. */
export function descargarRecetaFirmada(recetaId: number): Promise<void> {
  return abrirPdf(
    `/api/v1/portal/recetas/${recetaId}/firmada`,
    `receta-firmada-${recetaId}`,
  );
}

export function descargarResultados(ordenId: number): Promise<void> {
  return abrirPdf(
    `/api/v1/portal/resultados/${ordenId}/pdf`,
    `resultados-${ordenId}.pdf`,
  );
}
